/**
 * scheduleHelpers
 *
 * Shared medication schedule utilities used by care rows and the concept
 * calendar adapter. Keep form / timing / colour logic here so redesigned
 * surfaces do not reinvent medication presentation rules.
 *
 * Pure data layer: no React, no hooks.
 */

import { getMedicationByBrandName } from "@src/packages/content";
import type { MedicationBody } from "@src/packages/content/types";
import type { MedForm } from "./schedule.types";

// Map ApiMedication.form (CMS / regulatory forms) to the four canonical
// MedForm icon buckets the row renders.
export const FORM_MAP: Record<string, MedForm> = {
  tablet: "pill",
  capsule: "pill",
  oral_solution: "pill",
  sublingual_tablet: "pill",
  injection_solution: "vial",
  lyophilized_powder: "vial",
  pre_filled_pen: "pen",
  pre_filled_syringe: "vial",
  vaginal_gel: "suppository",
  vaginal_suppository: "suppository",
  transdermal_patch: "pill",
  nasal_spray: "vial",
  cream: "pill",
  other: "pill",
  // Identity mappings for content-module MedicationForm values
  pen: "pen",
  vial: "vial",
  pill: "pill",
  suppository: "suppository",
  patch: "pill",
  multi_vial: "vial",
};

// Map intakeType (standalone patient medications) to the form icon bucket.
export const INTAKE_TO_FORM: Record<string, MedForm> = {
  subcutaneous: "vial",
  intramuscular: "vial",
  intravenous: "vial",
  injection: "vial",
  oral: "pill",
  vaginal: "suppository",
  intravaginal: "suppository",
  topical: "suppository",
  patch: "pill",
  transdermal: "pill",
  intranasal: "vial",
  sublingual: "pill",
};

export function resolveFormFromContent(medName: string): MedForm | undefined {
  const content = getMedicationByBrandName(medName);
  if (!content) return undefined;
  const firstForm = (content.body as MedicationBody).commonForms?.[0];
  if (!firstForm) return undefined;
  return FORM_MAP[firstForm] ?? undefined;
}

export function timingFromTime(
  time: string,
): "morning" | "evening" | "as-needed" {
  const hour = parseInt(time.split(":")[0] ?? "9", 10);
  return hour < 12 ? "morning" : hour < 18 ? "as-needed" : "evening";
}

export function formatRoute(route: string | null): string {
  if (!route) return "";
  return route.charAt(0).toUpperCase() + route.slice(1);
}
